import React from "react"
import { Card, Icon } from "@mostlycode/ui"
import { graphql, Link } from "gatsby"
import { Elements } from "prismic-reactjs"
import { valuable } from "utils/worthy"
import { linkResolver } from "utils/linkResolver"
import styled from "styled-components"
import ResponsiveImage from "components/ResponsiveImage"
import Content from "components/Content"

const StyledCategory = styled(Card)`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  padding: 0;
  background: ${(props) =>
    props.display_style === "vertical"
      ? props.theme.colors.white
      : props.theme.colors.white};
  box-shadow: none;
  position: relative;
  transition: all ease-out 0.2s;
  transform: scale3d(1, 1, 1) translateY(0);
  filter: drop-shadow(0 10px 20px rgba(50, 50, 86, 0.25));

  &:hover {
    filter: drop-shadow(0 20px 40px rgba(50, 50, 86, 0.3));
    transform: scale3d(1.02, 1.02, 1.02) translateY(-2%);
  }

  h3 {
    margin: 0;
    font-size: 1.8rem;
    text-transform: uppercase;
  }

  p {
    font-size: 1.6rem;
  }

  figure {
    overflow: hidden;
    position: relative;
    margin: 0;
    padding: 0;
    border-radius: 5px 5px 0 0;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 93%);

    a {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10;
    }
  }

  > .category-content {
    margin-top: auto;
    color: ${(props) =>
      props.display_style === "vertical"
        ? props.theme.colors.kelp
        : props.theme.colors.kelp};
    position: relative;
    padding: 2rem 3rem;
    clip-path: polygon(0 0, 100% 13%, 100% 100%, 0% 100%);

    a {
      text-transform: uppercase;
      font-weight: bold;
      display: flex;
      align-items: center;
      line-height: 1;
      margin-top: 1rem;
      color: ${(props) =>
        props.display_style === "vertical"
          ? props.theme.colors.poppy
          : props.theme.colors.poppy};

      svg {
        margin-left: 0.5rem;
        fill: currentColor;
        width: 1.8rem;
        height: 1.8rem;
      }
    }
  }

  ${(props) => props.theme.media.tablet_landscape_up`
    & + & {
      margin-top: 0;
    }

    ${
      props.display_style === `horizontal`
        ? `grid-column: 1 / span ${props.columns};`
        : ""
    }
  `}
`

const htmlSerializer = function (type, element, content, children, key) {
  switch (type) {
    case Elements.heading1:
      return <h3 key={key}>{children}</h3>
    default:
      return null
  }
}

function Category({
  display_style,
  columns,
  category: { title, imageSharp, image, _meta },
  category_cta,
  category_summary,
}) {
  const link = linkResolver(_meta)
  return (
    <StyledCategory size="sm" display_style={display_style} columns={columns}>
      <figure>
        <Link to={link} />
        {display_style !== "horizontal" && (
          <ResponsiveImage img={imageSharp || image} alt={image.alt} />
        )}
      </figure>
      <article className="category-content">
        {valuable(title) && (
          <Content content={title} serializer={htmlSerializer} />
        )}
        <p>{category_summary}</p>
        <Link to={link}>
          <span>{category_cta}</span> <Icon icon="chevron-right" size="auto" />
        </Link>
      </article>
    </StyledCategory>
  )
}

export default Category

Category.defaultProps = {}
Category.propTypes = {}

export const query = graphql`
  fragment ProductCategoryFragment on PRISMIC_Product_category {
    title
    image
    imageSharp @include(if: $isProduction) {
      childImageSharp {
        id
        main: fluid(maxWidth: 400) {
          ...GatsbyImageSharpFluid_withWebp
        }
        mobile: fluid(maxWidth: 600) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    _meta {
      uid
      type
      id
      lang
    }
  }
`
